import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { CommonProps, RtnType, ShowPageProps } from "../../../utilsTs/declareTypes";
import AntDraggableModal from "../../universal/AntDraggableModal";
import { Form, FormInstance, ModalProps } from "antd"; // 新增 FormInstance、ModalProps 类型
import fetchApi from "./fetchApi";
import dayjs from "dayjs";
import antdMessage from './antdMessage';
import dateHandle from "../../../utilsTs/baseLib/dateHandle";

/**
 * 显示选项属性
 *
 * ============================================================
 *  【底部按钮（确定 / 取消）使用说明】
 * ------------------------------------------------------------
 *  组合方式均通过 `displayOptions` 传入，常用场景示例：
 *
 *  // ① 隐藏「确定」和「取消」两个按钮（footer 整体不渲染）
 *  displayOptions: { width: 1000, footer: null }
 *
 *  // ①.1 效果等价：只隐藏按钮，其它 footer 内容不涉及
 *  displayOptions: {
 *      okButtonProps:     { style: { display: 'none' } },
 *      cancelButtonProps: { style: { display: 'none' } },
 *  }
 *
 *  // ② 只显示「取消」（隐藏确定按钮）
 *  displayOptions: { okButtonProps: { style: { display: 'none' } } }
 *
 *  // ③ 只显示「确定」（隐藏取消按钮）
 *  displayOptions: { cancelButtonProps: { style: { display: 'none' } } }
 *
 *  // ④ 修改按钮文字/样式
 *  displayOptions: {
 *      okText: '保存',
 *      cancelText: '放弃',
 *      okButtonProps: { type: 'primary', danger: true },
 *      cancelButtonProps: { disabled: true },
 *  }
 *
 *  // ⑤ 完全自定义底部区域（ReactNode 形式）
 *  displayOptions: {
 *      footer: (
 *          <>
 *              <button onClick={() => {}}>关闭</button>
 *              <button onClick={() => {}}>保存并继续</button>
 *          </>
 *      ),
 *  }
 *
 *  // ⑥ 完全自定义并复用内部「校验 + 提交 / 取消」逻辑（函数形式）
 *  //    参数 ctx 由 BaseWebEditUniversal 注入，包含 onOk / onCancel
 *  displayOptions: {
 *      footer: ({ onOk, onCancel }) => (
 *          <>
 *              <Button onClick={onCancel}>放弃</Button>
 *              <Button type="primary" onClick={onOk}>保存草稿</Button>
 *          </>
 *      ),
 *  }
 *
 *  // ⑦ 关闭按钮（右上角 ×）/ 遮罩点击 关闭
 *  displayOptions: {
 *      closable: false,       // 不显示右上角 ×，默认 false（沿用原有行为）
 *      maskClosable: true,    // 点遮罩关闭，默认 false（沿用原有行为）
 *  }
 *
 * ============================================================
 */
export interface DisplayOptionsProps {
    /** 自动显示 */
    autoShow?: boolean;
    /** 确定按钮文字，默认：'确定' */
    okText?: string;
    /** 取消按钮文字，默认：'取消' */
    cancelText?: string;
    /** 窗口宽度，默认：700 */
    width?: number;
    /** 窗口高度，默认：自动 */
    height?: number;

    // ===== 新增：底部按钮 / 关闭行为控制 =====
    /**
     * 完全接管底部区域：
     *  - ReactNode：直接作为 Modal.footer 渲染
     *  - null：整个 footer 不渲染（不显示任何按钮）
     *  - 函数：(ctx: { onOk, onCancel }) => ReactNode
     *          可复用内部校验 + 提交 / 取消逻辑
     */
    footer?:
    | React.ReactNode
    | null
    | ((ctx: { onOk: () => void; onCancel: () => void }) => React.ReactNode);
    /**
     * 透传给 antd Modal 的 okButtonProps。
     * 隐藏确定按钮：{ style: { display: 'none' } }
     */
    okButtonProps?: ModalProps['okButtonProps'];
    /**
     * 透传给 antd Modal 的 cancelButtonProps。
     * 隐藏取消按钮：{ style: { display: 'none' } }
     */
    cancelButtonProps?: ModalProps['cancelButtonProps'];
    /** 右上角关闭 × 是否显示。默认沿用调用方原值（当前组件内部默认为 false） */
    closable?: boolean;
    /** 遮罩点击是否可关闭。默认沿用调用方原值（当前组件内部默认为 false） */
    maskClosable?: boolean;
};

type FormChildren = (options?: {}) => React.ReactNode;

/** 禁止选项属性 */
export interface DisabledOptionsProps {
    /** enter键执行，默认：true */
    isOnKeyDownEnter: boolean;
};

/** 编辑模式 */
export type EditMode = 'NEW' | 'EDIT';

/** BaseWebEditUniversal属性 */
export interface BaseWebEditUniversalProps extends
    Pick<CommonProps, 'apiId' | 'defaultService' | 'titleName' | 'primaryKeyName' | 'closeModal'>,

    Pick<ShowPageProps, 'sendPageData'> {
    /** 编辑模式，默认：'NEW' */
    editMode?: EditMode;
    /** 自动转换模式 */
    isAutoChangeMode?: boolean;
    /** enter键有没效 */
    isEnter?: boolean
    /** 编辑模式下获取数据的id值 */
    editingId?: string;
    /** form选项 */
    formOptions?: {
        /** 外部传入的 Form 实例（不传则内部自动创建） */
        instance?: FormInstance;
        /** 表单内容 */
        children?: FormChildren | React.ReactNode;
        /** form操作 */
        formOperation?: (data: any) => void;
    };
    /** 日期选项 */
    dateOptions?: {
        /** 【回显】后端返回的日期字段，读回时转 dayjs 给 DatePicker 显示 */
        columnNames?: Array<string>;
        /** 【提交】纯时间字段（后端 time 类型），提交时 dayjs → 'HH:mm:ss' */
        timeColumnNames?: Array<string>;
        /** 自动识别日期字段，未指定 columnNames 时生效，默认：false */
        autoDetect?: boolean;
    },
    /**
     * 显示选项
     * 参见文件顶部 DisplayOptionsProps 注释中的【底部按钮使用说明】
     */
    displayOptions?: DisplayOptionsProps;
    /** 禁用选项 */
    disabledOptions?: DisabledOptionsProps;
    /** 回调 */
    onCallbacks?: {
        /** 新增/编辑成功后触发，参数为接口返回体和当前模式 */
        onSuccess?: (res: RtnType, mode: EditMode) => void;
        /** 接口失败（success !== true）时触发 */
        onError?: (res: RtnType, mode: EditMode) => void;
    };
};

/**
 * 安全地把后端值转换为 dayjs 对象（零依赖，不依赖任何 dayjs 插件）
 * 支持：
 *   - 已是 dayjs 对象：原样返回
 *   - 数字时间戳：10位按秒处理（*1000），13位按毫秒处理
 *   - 数字字符串时间戳：同上
 *   - 纯时间字符串 "HH:mm" / "HH:mm:ss"：结合今天的日期构造，不走 dayjs(str)
 *   - 常规日期字符串：交给 dayjs 原生解析（仅对合法结果生效）
 *   - null / undefined / '' / 无法识别的值：原样返回，避免 Invalid Date 污染表单
 */
const toDayjsSafe = (value: any) => {
    // 空值保护：不要进 dayjs，否则会得到 Invalid Date
    if (value === null || value === undefined || value === '') return value;
    // 已经是 dayjs 对象直接返回
    if (dayjs.isDayjs(value)) return value;

    // 1) 数字：区分秒级(10位) / 毫秒级(13位)，阈值 1e12 作为兜底判断
    if (typeof value === 'number' && Number.isFinite(value)) {
        const ms = Math.abs(value) < 1e12 ? value * 1000 : value;
        const d = dayjs(ms);
        return d.isValid() ? d : value;
    }

    // 2) 字符串：分别处理纯时间、时间戳、常规日期
    if (typeof value === 'string') {
        const s = value.trim();
        if (s === '') return value;

        // 2.1) 纯时间 "HH:mm" 或 "HH:mm:ss" —— 手工构造，避免 dayjs(s) 解析失败
        const timeMatch = s.match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
        if (timeMatch) {
            const h = +timeMatch[1];
            const m = +timeMatch[2];
            const sec = timeMatch[3] ? +timeMatch[3] : 0;
            // 合法性校验，避免构造出非法时间
            if (h < 24 && m < 60 && sec < 60) {
                return dayjs().hour(h).minute(m).second(sec).millisecond(0);
            }
            return value;
        }

        // 2.2) 数字字符串时间戳：10位=秒，13位=毫秒
        if (/^\d{10}$/.test(s)) return dayjs(Number(s) * 1000);
        if (/^\d{13}$/.test(s)) return dayjs(Number(s));

        // 2.3) 常规日期字符串（ISO 格式 dayjs 原生即支持，如 "2024-01-01 12:30:00"）
        const d = dayjs(s);
        return d.isValid() ? d : value;
    }

    // 其他类型原样返回
    return value;
};

/**
 * 基础通用编辑组件
 */
const BaseWebEditUniversal = forwardRef<any, BaseWebEditUniversalProps>((props, ref) => {
    // editMode 由 props 派生，拆成 initEditMode（外部传入的初始/受控值）与内部 state
    let { apiId, editMode: initEditMode = 'NEW', isAutoChangeMode = false, editingId, titleName, defaultService, displayOptions: initDisplayOptions,
        disabledOptions: initDisabledOptions, isEnter = true, primaryKeyName = 'id', formOptions, dateOptions,
        closeModal, sendPageData, onCallbacks
    } = props;
    // 优先使用外部传入的 form，否则创建一个内部的
    const [internalForm] = Form.useForm();
    const form = formOptions?.instance ?? internalForm;
    const { columnNames, timeColumnNames, autoDetect = false } = dateOptions || {};
    // 内部可变的编辑模式（props 派生值修改必须走 state，否则不会触发重渲染）
    const [editMode, setEditMode] = useState<EditMode>(initEditMode);
    // 外部 editMode 变化时同步到内部 state
    useEffect(() => {
        setEditMode(initEditMode);
    }, [initEditMode]);
    //显示选项
    const [displayOptions, setDisplayOptions] = useState<DisplayOptionsProps>({
        autoShow: initDisplayOptions?.autoShow ?? true,
        okText: initDisplayOptions?.okText ?? '确定',
        cancelText: initDisplayOptions?.cancelText ?? '取消',
        width: initDisplayOptions?.width ?? 700,
        height: initDisplayOptions?.height,

        // ===== 新增：底部按钮 / 关闭行为控制 =====
        // footer 支持 ReactNode | null | (ctx) => ReactNode，
        // 未传时保持 antd Modal 默认 footer（即 okText/cancelText + 默认按钮）
        footer: initDisplayOptions?.footer,
        okButtonProps: initDisplayOptions?.okButtonProps,
        cancelButtonProps: initDisplayOptions?.cancelButtonProps,
        // closable / maskClosable 不设置默认值，渲染时用 ?? false 兜底，
        // 保持组件原有行为（右上角 × 不显示、遮罩点击不关闭）
        closable: initDisplayOptions?.closable,
        maskClosable: initDisplayOptions?.maskClosable,
    });
    //禁用选项
    const [disabledOptions, setDisabledOptions] = useState<DisabledOptionsProps>({
        isOnKeyDownEnter: initDisabledOptions?.isOnKeyDownEnter ?? true,
    });
    const [getRow] = useState<Promise<RtnType> | null>(() => {
        if (initEditMode === 'EDIT' && editingId) {
            return fetchApi.GET(defaultService, {
                query: {
                    apiId,
                    apiVariable: editingId,
                    apiData: {
                        sendPageData
                    }
                }
            });
        }
        return null;
    });

    const [rowData, setRowData] = useState<any>(null);

    // ===== 事件处理函数 =====
    const handleOk = () => {
        form.validateFields() //数据校验
            .then(async (values: any) => {
                const editData = {
                    ...values,
                    sendPageData,
                };
                // 纯时间字段（如 pointInTime，后端 time 类型）：dayjs → 'HH:mm:ss'
                // 必须用 format 取本地时间，不能用 toISOString()（会转 UTC 导致时间错位）
                if (timeColumnNames && timeColumnNames.length > 0) {
                    for (const col of timeColumnNames) {
                        if (col in editData && dayjs.isDayjs(editData[col])) {
                            editData[col] = editData[col].format('HH:mm:ss');
                        }
                    }
                }
                switch (editMode) {
                    case 'NEW':
                        fetchApi.POST(defaultService, {
                            body: {
                                apiId,
                                apiVariable: editingId,
                                apiData: editData
                            }
                        }).then(res => {
                            if (res.success === true) {
                                antdMessage('success', '新增成功');
                                onCallbacks?.onSuccess?.(res, 'NEW');   // ← 成功回调
                                closeModal?.({ success: true });
                            } else {
                                antdMessage('error', '新增失败', { description: res?.message });
                                onCallbacks?.onError?.(res, 'NEW');     // ← 业务失败回调
                            }
                        }).catch(err => {
                            antdMessage('error', '错误，无法保存', { description: err.message + ' | status：' + err.errors.status });
                            onCallbacks?.onError?.(err as any, 'NEW');  // ← 网络/异常失败回调
                        })
                        break;
                    case 'EDIT':
                        fetchApi.PUT(defaultService, {
                            body: {
                                apiId,
                                apiVariable: editingId,
                                apiData: editData
                            }
                        }).then(res => {
                            if (res.success === true) {
                                antdMessage('success', '编辑成功');
                                onCallbacks?.onSuccess?.(res, 'EDIT');  // ← 成功回调
                                closeModal?.({ success: true });
                            } else {
                                antdMessage('error', '编辑错误', { description: res.message });
                                onCallbacks?.onError?.(res, 'EDIT');    // ← 业务失败回调
                            }

                        }).catch(err => {
                            antdMessage('error', '错误，无法保存', { description: err.message + ' | status：' + err.errors.status });
                            onCallbacks?.onError?.(err as any, 'EDIT'); // ← 网络/异常失败回调
                        })
                        break;
                }
            })
            .catch((err: any) => {
                antdMessage('warning', '无法通过数据校验', { description: '请输入所需数据！' });
            });
    };
    const handleCancel = () => {
        closeModal?.();
    };

    useEffect(() => {
        getRow?.then((rtn: RtnType) => {
            if (rtn.success === true) {
                setRowData(rtn.data);
                const data = { ...rtn.data };
                // 日期字段转换
                if (columnNames && columnNames.length > 0) {
                    // 如果明确指定了日期列，则直接转换这些列（信任配置）
                    // 使用 toDayjsSafe 统一处理：兼容 null/空串、秒级时间戳、纯时间字符串等
                    for (const col of columnNames) {
                        if (col in data) {
                            data[col] = toDayjsSafe(data[col]);
                        }
                    }
                } else {
                    if (autoDetect) {
                        // 未指定日期列时，自动识别日期/时间字段
                        // 使用严格判断，避免纯数字（如数量、编号）被误转为 dayjs 对象
                        for (const columnName in data) {
                            const value = data[columnName];

                            // 数字：仅当为 10 位（秒）或 13 位（毫秒）时间戳时才转换
                            if (typeof value === 'number') {
                                const len = String(Math.abs(value)).length;
                                if (len === 10 || len === 13) {
                                    data[columnName] = toDayjsSafe(value);
                                }
                                continue;
                            }

                            // 字符串：严格日期字符串 / 纯时间字符串 / 数字字符串时间戳
                            if (typeof value === 'string') {
                                const s = value.trim();
                                const isTime = /^\d{1,2}:\d{1,2}(?::\d{1,2})?$/.test(s);
                                const isTs = /^\d{10}$|^\d{13}$/.test(s);
                                if (isTime || isTs || dateHandle.isStrictDateString(s)) {
                                    data[columnName] = toDayjsSafe(value);
                                }
                            }
                        }
                    }
                }
                form.setFieldsValue(data);
                if (formOptions?.formOperation) {
                    formOptions?.formOperation(data);
                }
            } else {
                if (isAutoChangeMode === true) {
                    // 使用 setState，触发重渲染，标题等 UI 同步更新
                    setEditMode('NEW');
                }
            }
        });
    }, [initEditMode]);

    // ==================== 暴露给父组件的方法 ====================
    useImperativeHandle(ref, () => ({
        /** 获取数据 */
        getRow,
    }));

    // ===== 计算底部 footer 节点 =====
    // 说明：
    //  - displayOptions.footer 未传（undefined）：走 antd Modal 默认 footer（使用 okText/cancelText）
    //  - displayOptions.footer 为 null       ：完全隐藏 footer（两个按钮都不显示）
    //  - displayOptions.footer 为 ReactNode  ：直接作为 Modal.footer
    //  - displayOptions.footer 为函数         ：调用后返回 ReactNode，并注入 { onOk, onCancel }
    //                                          让自定义按钮可复用内部校验/提交/取消逻辑
    const footerNode: React.ReactNode | undefined = (() => {
        const f = displayOptions.footer;
        if (f === undefined) return undefined;           // 未设置：交给 antd 默认
        if (f === null) return null;                     // 显式隐藏
        if (typeof f === 'function') {
            return (f as (ctx: { onOk: () => void; onCancel: () => void }) => React.ReactNode)({
                onOk: handleOk,
                onCancel: handleCancel,
            });
        }
        return f;                                        // ReactNode
    })();

    return (
        <AntDraggableModal
            title={`${(editMode === 'NEW' ? '新增' : '编辑')}${titleName}`}
            open={true}
            okText={displayOptions.okText}
            cancelText={displayOptions.cancelText}
            onOk={handleOk}
            onCancel={handleCancel}
            // closable / maskClosable 走 ?? false 保持原有行为
            closable={displayOptions.closable ?? false}
            // mask={{ closable: displayOptions.maskClosable ?? false }}
            width={displayOptions.width}
            height={displayOptions.height}
            // ===== 新增透传：底部按钮区域 =====
            // footerNode 为 undefined 时 antd 会走默认 footer，等价于旧行为
            footer={footerNode}
            okButtonProps={displayOptions.okButtonProps}
            cancelButtonProps={displayOptions.cancelButtonProps}
        >
            <Form
                form={form}
                name="Edit"
                onFinish={handleOk}
                onKeyDown={(e: any) => {
                    if (isEnter) {
                        if (e.key === 'Enter' && disabledOptions.isOnKeyDownEnter) {
                            handleOk()
                        }
                    }

                }}
            >
                {typeof formOptions?.children === 'function'
                    ? formOptions.children({ getRow })
                    : formOptions?.children}
            </Form>
        </AntDraggableModal>
    )
});

export default BaseWebEditUniversal;